import { CDN_PUB_URL_BELLRINGERS, NA } from "../../constants"
import { BellringersRest } from "./bellringers-rest-interface"

export const BELLRINGER_SUBJECTS = 3
export const WEEKLY_QUESTIONS_PER_SUBJECT = 3

export const BELLRINGER_MAX_WEEKS = 30
export const BELLRINGER_TABLE_WEEK_NUMBERS = Array.from(Array(BELLRINGER_MAX_WEEKS).keys()).map(k => k + 1) // Weeks are 1-indexed
export const BELLRINGER_QUESTION_NUMBERS = [1, 2, 3] // Questions are 1-indexed

export interface IBellringersTestQuestion {
    id: string
    subject: string
    grade: number
    week: number
    questionNo: number
    levelOfDifficulty: string
    optionA: string
    optionB: string
    optionC: string
    optionD: string
    questionImage: string
    questionQualifier: string
    questionText: string
    waLesson: string
    waTopic: string
    satDomain: string
    satSkill: string

    // User added values
    userSubmittedAnswer?: string
    bookmarked?: boolean
    crossedOutInfo?: any
}

// Extended Bellringer question for teachers!
export interface IBellringersTestQuestionEx extends IBellringersTestQuestion {
    correctAnswer?: string
    explanationA: string
    explanationB: string
    explanationC: string
    explanationD: string
}

export interface IBellringerTestUserAnswer {
    userId: string
    bellringerId: string
    emailAddress: string
    correctAnswer: string
    userSubmittedAnswer: string
    bookmarked: boolean
    correct: boolean
    studyTimeInSeconds: number
    dateCreatedAt: string
    dateUpdatedAt: string
}

export interface IStudentBellringersWithMetaData {
    userBellringers: IBellringerTestUserAnswer[]
    bellringersMetaDataMap: IBellringerMetaDataMap
}

export type BellringerDifficulty = "Easy" | "Medium" | "Hard"

export interface IBellringerMetaData {
    id: string
    waLesson: string
    waTopic: string
    levelOfDifficulty: BellringerDifficulty // Easy | Medium | Hard
}

export interface IBellringerMetaDataMap {
    [bellringerId: string]: IBellringerMetaData
}

export interface IBellringerGroup {
    bellringer: IBellringersTestQuestion
    userBellringer?: any
}

export interface IBellringersTestQuestionWithUserAnswers extends 
    IBellringersTestQuestion, IBellringersTestQuestionEx, Partial<IBellringerTestUserAnswer> { }

/**
 * Gets the weekly questions for the subject, grade and week.
 * Every subject has 3 questions!
 * 
 * @param bellringersTestId <subject>-<grade>-<week>
 */
export async function getBellringersTestQuestions(bellringersTestId): Promise<IBellringersTestQuestion[]> {
    let weeklyQuestionsWithUserAnswers = await BellringersRest.getBellringersWeeklyTestQuestionsWithUserAnswers(bellringersTestId)
    let blendedQuestions: IBellringersTestQuestion[] = []
    
    if (!weeklyQuestionsWithUserAnswers) return []

    for (let i=0;i < WEEKLY_QUESTIONS_PER_SUBJECT;i++) {
        let weeklyQs = weeklyQuestionsWithUserAnswers[i]
        if (weeklyQs) {
            let {bellringer, userBellringer} = weeklyQs
            let blendedQuestion = {...bellringer, ...userBellringer} as IBellringersTestQuestion
            blendedQuestions.push(blendedQuestion)
        }
    }

    return blendedQuestions
}

/**
 * Number of weeks: 
 * Grade  9: 15 weeks
 * Grade 10: 30 weeks
 * Grade 11: 30 weeks
 * Grade 12: 15 weeks
 * 
 * @param grade 
 * @param userBellringersData 
 * @returns 
 */
function prepareWeeksForGrade(grade, userBellringersData: any[]) {
    let nofWeeks = grade === 9 || grade === 12 ? 15: 30
    let weeksArray = Array.from(Array(nofWeeks).keys())
    let weeks = weeksArray.map(weekIndex => {
        return {
            weekIndex,
            week: `Week ${weekIndex+1}`,
            english: [],
            math: [],
            reading: [],
            science: []
        }
    })

    // Find the proper subject, grade and week to add the questions
    userBellringersData.forEach(br => {
        let {bellringerId, userSubmittedAnswer, correctAnswer, correct} = br
        let {subject, grade: grade2, week, questionNumber} = getBellringerQuestionMetaData(bellringerId)

        if (grade === grade2) {
            let weekIndex = week - 1
            let currentWeek = weeks[weekIndex]
            // console.log("OT: CURRENT WEEK->", currentWeek)
            currentWeek[subject].push({
                question: questionNumber,
                userSubmittedAnswer,
                correctAnswer,
                correct
            })
        }
    })

    return weeks
}

/**
 * Prepare the grid based info for the grades, weeks and subjects
 * 
 * @param userBellringersData 
 * @returns 
 */
function prepareGrid(userBellringersData) {
    let grid = [
        { grade:  9, set: 1, weeks: prepareWeeksForGrade(9, userBellringersData)},
        { grade: 10, set: 2, weeks: prepareWeeksForGrade(10, userBellringersData)},
        { grade: 11, set: 3, weeks: prepareWeeksForGrade(11, userBellringersData)},
        { grade: 12, set: 4, weeks: prepareWeeksForGrade(12, userBellringersData)},
    ]

    return grid
}

/**
 * Prepare the students' grid data
 * @returns 
 */
export async function getStudentGridData() {
    let userBellringers = await BellringersRest.getAllUserBellringers()
    let grid = prepareGrid(userBellringers)
    return grid
}

// Gets the BR Set# based on the grade (per Jennifer's recent request we will use Set#)
// Grade 9 -> Set 1, etc.
export function getBellringerSetNumber(grade: number|string) {
    let gradeNumber = typeof grade === "number" ? grade : Number(grade)
    return gradeNumber - 8
}

/**
 * Gets the bellringer for review (i.e. combined bellringer question with user's answer)
 * 
 * @param bellringerId 
 * @returns 
 */
export async function getBellringerQuestionForReview(bellringerId: string): Promise<IBellringersTestQuestionEx> {
    let userBellringer = await BellringersRest.getUserBellringer(bellringerId)
    let bellringer = await BellringersRest.getBellringer(bellringerId)
    let combinedBellringer = {...bellringer, ...userBellringer}

    return combinedBellringer
}

/**
 * Get the full question with the user's entries for review.
 * @param bellringerId 
 * @returns 
 */
export async function upsertUserBellringer(bellringerId: string, userSubmittedAnswer: string, bookmarked=false, studyTimeInSeconds=0) {
    return BellringersRest.upsertUserBellringer(bellringerId, userSubmittedAnswer, bookmarked, studyTimeInSeconds)
}

export function getBellringersImageURL(questionImage: string) {
    if (!questionImage) return

    return questionImage.replace("$CDN", CDN_PUB_URL_BELLRINGERS)
}

export function getBellringersCleanContent(content: string) {
    if (!content) return

    return content.replace("$CDN", CDN_PUB_URL_BELLRINGERS)
}

export function getBellringerTestMetaData(bellringersTestId: string) {
    let [subject, grade, week] = bellringersTestId.split("-")
    let metaData = {subject, grade: Number(grade), week: Number(week)}
    return metaData
}

export function getBellringerQuestionMetaData(bellringerId: string) {
    let [subject, grade, week, questionNumber] = bellringerId.split("-")
    let metaData = {subject, grade: Number(grade), week: Number(week), questionNumber: Number(questionNumber)}
    return metaData
}

/**
 * Gets students' BR test results
 */
export async function getStudentTestRestultsWithMetaDataForTeacher() {
    return await BellringersRest.getStudentTestRestultsWithMetaDataForTeacher()
}

// Bellringer Student
interface IBellringerStudent {
    firstName: string
    lastName: string
    emailAddress: string
    schoolName: string
    graduationYear: number
    period: string
    bellringers: IBellringerTestUserAnswer[]
}

// Blended/Combined info for Bellringers
export function getBlendedStudentsInfo(students, timeOnPlatformInfo, bellringersInfo): IBellringerStudent[] {
    let blendedStudentsInfo = students.map(student => {
        let {id: userId, firstName, lastName, emailAddress, graduationYear, schoolName, period} = student
        return {
            userId,
            firstName, lastName, emailAddress, 
            graduationYear: Number(graduationYear),
            schoolName,
            period,
            bellringers: bellringersInfo.filter(bellringer => bellringer.userId === userId)
        }
    })
    return blendedStudentsInfo
}

export function getGraduationYears(studentBellringers: IBellringerStudent[]): number[] {
    let graduationYearsSet = new Set<number>()
    for (let bellringer of studentBellringers) {
        if (bellringer.graduationYear) {
            graduationYearsSet.add(Number(bellringer.graduationYear))
        }
    }
    return Array.from(graduationYearsSet).sort((a, b) => a - b)
}

export function getPeriods(studentBellringers: IBellringerStudent[]): string[] {
    if (!studentBellringers) return []

    let set1 = new Set<string>()
    for (let bellringer of studentBellringers) {
        // OT: HACK to handle unspecified periods! 
        // According to Jennifer there are unknown/unspecied periods that we still need to take care!
        if (!bellringer.period) { 
            bellringer.period = ""
        }
        set1.add(bellringer.period)
    }
    return Array.from(set1).sort((a, b) => a.localeCompare(b))
}

export interface IQuestionStats {
    bellringerId: string
    correctAnswer: string
    subject: string
    period?: string
    setNumber: number
    grade: number
    week: number
    questionNumber: number
	bookmarkedCount: number

	scoreAverage: number
	scoreMin: number
	scoreMax: number

	studyTimeTotal: number
	studyTimeAverage: number
	studyTimeMin: number
	studyTimeMax: number

    // Users' Answers
	answerTotal: number
	answerCorrect: number
    answerCountA: number
    answerCountB: number
    answerCountC: number
    answerCountD: number

    // Distractor answers (More people put wrong answer than right!)
    distractorCountTotal: number
    distractorCountA: number
    distractorCountB: number
    distractorCountC: number
    distractorCountD: number
    distractorAnswer: string 
}

function processDistactor(stats: IQuestionStats) {
    if (stats.distractorCountTotal > 0) {
        // Find out which answer is the distractor!
        let max = stats.distractorCountA

        stats.distractorAnswer = "A"

        if (stats.distractorCountB > max) { 
            max = stats.distractorCountB
            stats.distractorAnswer = "B"
        }

        if (stats.distractorCountC > max) {
            max = stats.distractorCountC
            stats.distractorAnswer = "C" 
        }

        if (stats.distractorCountD > max) {
            max = stats.distractorCountD
            stats.distractorAnswer = "D"
        }
    }
}

function processBellringerStats(bellringer: IBellringerTestUserAnswer, statsMap: Record<string, IQuestionStats>, period?: string) {
    let {bellringerId, correctAnswer, userSubmittedAnswer} = bellringer
    let key = period ? `${bellringerId}-${period}`: `${bellringerId}`
	let stats = statsMap[key]

    if (!stats) {
        let {subject, grade, week, questionNumber } = getBellringerQuestionMetaData(bellringerId!)
        stats = {
            bellringerId,
            correctAnswer,
            subject,
            setNumber: grade - 8,
            period,
            grade,
            week,
            questionNumber,
            answerTotal: 0,
            answerCorrect: 0,
            bookmarkedCount: 0,

            scoreAverage: 0,
            scoreMin: Infinity,
            scoreMax: -Infinity,

            studyTimeTotal: 0,
            studyTimeAverage: 0,
            studyTimeMin: Infinity,
            studyTimeMax: -Infinity,

            answerCountA: 0,
            answerCountB: 0,
            answerCountC: 0,
            answerCountD: 0,

            distractorCountTotal: 0,
            distractorCountA: 0,
            distractorCountB: 0,
            distractorCountC: 0,
            distractorCountD: 0,
            distractorAnswer: NA
        }
        statsMap[key] = stats
    }

    stats.answerTotal++

    let score = bellringer.correct ? 1 : 0
    let studyTime = bellringer.studyTimeInSeconds || 0

    if (bellringer.correct) stats.answerCorrect++
    if (bellringer.bookmarked) stats.bookmarkedCount++

    stats.scoreMin = Math.min(stats.scoreMin, score)
    stats.scoreMax = Math.max(stats.scoreMax, score)

    stats.studyTimeTotal += studyTime
    stats.studyTimeMin = Math.min(stats.studyTimeMin, studyTime)
    stats.studyTimeMax = Math.max(stats.studyTimeMax, studyTime)

    if (userSubmittedAnswer === "A") stats.answerCountA++
    if (userSubmittedAnswer === "B") stats.answerCountB++
    if (userSubmittedAnswer === "C") stats.answerCountC++
    if (userSubmittedAnswer === "D") stats.answerCountD++

    if (userSubmittedAnswer !== correctAnswer) {
        stats.distractorCountTotal++
        if (userSubmittedAnswer === "A") stats.distractorCountA++
        if (userSubmittedAnswer === "B") stats.distractorCountB++
        if (userSubmittedAnswer === "C") stats.distractorCountC++
        if (userSubmittedAnswer === "D") stats.distractorCountD++
    }
}

export function getStatsForAllQuestions(bellringerStudents: IBellringerStudent[]) {
	let questionStatsMap: Record<string, IQuestionStats> = {}

	for (let student of bellringerStudents) {
        let {bellringers} = student
		for (let bellringer of bellringers) {
            processBellringerStats(bellringer, questionStatsMap)
		}
	}

	for (let stats of Object.values(questionStatsMap)) {
		stats.scoreAverage = stats.answerCorrect / stats.answerTotal
		stats.studyTimeAverage = stats.studyTimeTotal / stats.answerTotal

        processDistactor(stats)
	}

    // Turn into a list
    let list = Object.keys(questionStatsMap).map(key => {
        let data = questionStatsMap[key]
        return {...data}
    })

    return list
}


export function getStatsForAllQuestionsPerPeriod(bellringerStudents: IBellringerStudent[]): IQuestionStats[] {
    let statsMap: Record<string, IQuestionStats> = {}

    for (let student of bellringerStudents) {
        let {period, bellringers} = student
		for (let bellringer of bellringers) {
            processBellringerStats(bellringer, statsMap, period)
		}
	}

    for (let stats of Object.values(statsMap)) {
		stats.scoreAverage = stats.answerCorrect / stats.answerTotal
		stats.studyTimeAverage = stats.studyTimeTotal / stats.answerTotal
        processDistactor(stats)
	}

    // Turn into a list
    let list = Object.keys(statsMap).map(key => {
        let data = statsMap[key]
        return {...data}
    })

    return list
}

export function getStatsForSetAndSubject(stats, setNumber, subject) {
    return stats.filter(d => d.setNumber === setNumber && d.subject === subject)
}

export interface IQuestionReviewInfoWithStats {
    bellringerId: string
    correctAnswer: string
    distractorAnswer: string // A|B|C|D or N/A (ignore if N/A or empty)
    subject: string
    week: number
    grade: number
    setNumber: number
    period: string
    questionNumber: number

    // Human readable info in UI
    displayInfo: {
        infoOverallAccuracy: string
        infoAverageTime: string
        infoAverageLowest: string
        infoAverageHighest: string
    }

    // Raw stats data
    stats: {
        answerTotal: number
        answerCorrect: number

        scoreAverage: number
        scoreMin: number
        scoreMax: number
        studyTimeTotal: number

        studyTimeAverage: number
        studyTimeMin: number
        studyTimeMax: number

        answerCountA: number
        answerCountB: number
        answerCountC: number
        answerCountD: number

        distractorCountTotal: number
        distractorCountA: number
        distractorCountB: number
        distractorCountC: number
        distractorCountD: number
    }

    // Bellringer question with explanations (only for teachers) 
    question: {
        questionNo: number
        subject: string
        grade: number
        week: number
        levelOfDifficulty: string
        questionId: string
        satDomain: string
        satSkill: string
        waLesson: string
        waTopic: string
        questionImage: string
        questionText: string
        questionQualifier: string
        correctAnswer: string
        optionA: string
        optionB: string
        optionC: string
        optionD: string
        explanationA: string
        explanationB: string
        explanationC: string
        explanationD: string
    }
}

export function getQuestionReviewInfoForTeacher(question: IBellringersTestQuestionEx, questionStats: IQuestionStats): IQuestionReviewInfoWithStats {
    let {bellringerId, subject, week, grade, setNumber, questionNumber, period="", 
        correctAnswer, answerTotal, answerCorrect, answerCountA, answerCountB, answerCountC, answerCountD,
        scoreAverage, scoreMin, scoreMax,
        studyTimeTotal, studyTimeAverage, studyTimeMin, studyTimeMax, 
        distractorAnswer, distractorCountTotal, distractorCountA, distractorCountB, distractorCountC, distractorCountD,
    } = questionStats
    let infoOverallAccuracy = `${Math.round(100*scoreAverage)}%`
    let infoAverageTime = `${Math.round(studyTimeAverage)} sec`
    let infoAverageLowest = `${studyTimeMin} sec`
    let infoAverageHighest = `${studyTimeMax} sec`

    return {
        bellringerId,
        correctAnswer,
        distractorAnswer,
        subject,
        week,
        grade,
        setNumber,
        period,
        questionNumber,

        // Human readable info in UI
        displayInfo: {
            infoOverallAccuracy,
            infoAverageTime,
            infoAverageLowest,
            infoAverageHighest,
        },

        // Raw stats data
        stats: {
            answerTotal,
            answerCorrect,
            scoreAverage,
            scoreMin,
            scoreMax,
            studyTimeTotal,
            studyTimeAverage,
            studyTimeMin,
            studyTimeMax,
            answerCountA,
            answerCountB,
            answerCountC,
            answerCountD,
            distractorCountTotal,
            distractorCountA,
            distractorCountB,
            distractorCountC,
            distractorCountD,
        },

        // Bellringer question with explanations (only for teachers)
        question: {
            questionNo: question.questionNo,
            subject: question.subject,
            grade: question.grade,
            week: question.week,
            levelOfDifficulty: question.levelOfDifficulty,
            questionId: question.id,
            satDomain: question.satDomain,
            satSkill: question.satSkill,
            waLesson: question.waLesson,
            waTopic: question.waTopic,
            questionImage: question.questionImage,
            questionText: question.questionText,
            questionQualifier: question.questionQualifier,
            correctAnswer: question.correctAnswer || "",
            optionA: question.optionA,
            optionB: question.optionB,
            optionC: question.optionC,
            optionD: question.optionD,
            explanationA: question.explanationA,
            explanationB: question.explanationB,
            explanationC: question.explanationC,
            explanationD: question.explanationD,
        },
    }
}

